import React, { useState, useEffect } from "react";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Eye, MessageSquare } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { useSmsTemplates } from "@/hooks/useSmsTemplates";
import { previewSmsMessage, getAvailableVariables } from "@/utils/smsUtils";
import type { CampaignFormData } from "./modal/CampaignFormSchema";

export const SmsSection = () => {
  const form = useFormContext<CampaignFormData>();
  const { templates } = useSmsTemplates();
  const [useCustomMessage, setUseCustomMessage] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const defaultTemplate = templates.find(t => t.is_default);
  const currentMessage = form.watch("sms_message");
  const selectedTemplateId = form.watch("sms_template_id");

  // Reset template selection when switching to custom message
  useEffect(() => {
    if (useCustomMessage) {
      form.setValue("sms_template_id", undefined);
    } else {
      form.setValue("sms_message", undefined);
      if (defaultTemplate && !selectedTemplateId) {
        form.setValue("sms_template_id", defaultTemplate.id);
      }
    }
  }, [useCustomMessage, defaultTemplate, form, selectedTemplateId]);

  const getCurrentMessage = () => {
    if (useCustomMessage && currentMessage) {
      return currentMessage;
    }
    
    if (selectedTemplateId) {
      const template = templates.find(t => t.id === selectedTemplateId);
      return template?.message || "";
    }
    
    return defaultTemplate?.message || "";
  };

  const availableVariables = getAvailableVariables();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          Notificação SMS
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Toggle entre template padrão e mensagem customizada */}
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <label className="text-sm font-medium">Tipo de mensagem</label>
            <p className="text-xs text-muted-foreground">
              {useCustomMessage ? "Mensagem personalizada para esta campanha" : "Usar template padrão"}
            </p>
          </div>
          <Switch
            checked={useCustomMessage}
            onCheckedChange={setUseCustomMessage}
          />
        </div>

        {useCustomMessage ? (
          // Mensagem customizada
          <FormField
            control={form.control}
            name="sms_message"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Mensagem SMS Personalizada</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    placeholder="Digite sua mensagem personalizada..."
                    className="min-h-[100px]"
                    maxLength={160}
                  />
                </FormControl>
                <div className="flex justify-between items-center text-xs text-muted-foreground">
                  <span>{(field.value || "").length}/160 caracteres</span>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        ) : (
          // Seleção de template
          <FormField
            control={form.control}
            name="sms_template_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Template SMS</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione um template" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {templates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        <div className="flex items-center gap-2">
                          {template.name}
                          {template.is_default && (
                            <Badge variant="secondary" className="text-xs">
                              Padrão
                            </Badge>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {/* Variáveis disponíveis */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Variáveis disponíveis:</label>
          <div className="flex flex-wrap gap-1">
            {availableVariables.map((variable) => (
              <Badge key={variable} variant="outline" className="text-xs">
                {`{${variable}}`}
              </Badge>
            ))}
          </div>
        </div>

        {/* Preview da mensagem */}
        <div className="space-y-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center gap-2"
          >
            <Eye className="h-4 w-4" />
            {showPreview ? "Ocultar Preview" : "Ver Preview"}
          </Button>

          {showPreview && (
            <div className="p-3 bg-muted rounded-md">
              <p className="text-sm font-medium mb-2">Preview da mensagem:</p>
              <p className="text-sm bg-background p-2 rounded border">
                {previewSmsMessage(getCurrentMessage())}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {previewSmsMessage(getCurrentMessage()).length}/160 caracteres
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};